<template>
  <div class="terms-reminders-table">
    <article
      v-for="expert in experts"
      :key="expert.expert_id"
      class="expert-reminder-card card border-0 shadow-sm mb-3"
    >
      <div
        class="card-header expert-toggle border-0"
        role="button"
        tabindex="0"
        :aria-expanded="isExpanded(expert.expert_id) ? 'true' : 'false'"
        @click="toggle(expert.expert_id)"
        @keydown.enter.prevent="toggle(expert.expert_id)"
        @keydown.space.prevent="toggle(expert.expert_id)"
      >
        <div class="d-flex align-items-start justify-content-between flex-wrap gap-3">
          <div class="d-flex align-items-start">
            <span class="expert-icon me-3">
              <i class="fa fa-user" />
            </span>
            <span>
              <a
                :href="getExpertUrl(expert.expert_id)"
                target="_blank"
                class="d-block expert-name expert-link"
                @click.stop
              >
                {{ expert.expert_name || "N/A" }}
              </a>
              <span class="expert-meta">
                Latest reminder:
                <span v-if="latestReminder(expert)">
                  {{ formatLocalTime(latestReminder(expert).sent_at) }}
                </span>
                <span v-else>N/A</span>
              </span>
            </span>
          </div>
          <div class="d-flex align-items-center gap-2">
            <expert-terms-reminder-button
              :expert="reminderButtonExpert(expert)"
              @reminder-sent="$emit('reminder-sent')"
            />
            <span class="badge rounded-pill reminder-count-badge">
              {{ expert.reminder_count }} {{ expert.reminder_count === 1 ? 'reminder' : 'reminders' }}
            </span>
            <i
              :class="isExpanded(expert.expert_id) ? 'fa fa-chevron-up' : 'fa fa-chevron-down'"
              class="chevron"
            />
          </div>
        </div>
      </div>

      <div
        v-if="isExpanded(expert.expert_id)"
        class="card-body p-0"
      >
        <div class="table-responsive">
          <table class="table reminder-detail-table mb-0 align-middle">
            <thead>
              <tr>
                <th>Sent At</th>
                <th>Project</th>
                <th>Sent By</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="reminder in sortedReminders(expert.reminders)"
                :key="reminder.id"
              >
                <td style="width: 33%;">
                  <span v-if="reminder.sent_at">{{ formatLocalTime(reminder.sent_at) }}</span>
                  <span v-else>N/A</span>
                </td>
                <td style="width: 34%;">
                  <a
                    v-if="reminder.project_id"
                    :href="getProjectUrl(reminder.project_id)"
                    target="_blank"
                    class="project-link"
                    @click.stop
                  >{{ reminder.project_title || "N/A" }}</a>
                  <span v-else>{{ reminder.project_title || "N/A" }}</span>
                </td>
                <td style="width: 33%;">
                  {{ reminder.sent_by_name || "N/A" }}
                </td>
              </tr>
              <tr v-if="!expert.reminders || expert.reminders.length === 0">
                <td
                  colspan="3"
                  class="text-center text-muted py-4"
                >
                  No reminders for this expert.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </article>
  </div>
</template>

<script>
import ExpertTermsReminderButton from "./ExpertTermsReminderButton.vue";

export default {
    name: "ExpertTermsRemindersTable",
    components: {
        ExpertTermsReminderButton,
    },
    props: {
        experts: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            expanded: {},
        };
    },
    methods: {
        toggle(id) {
            this.$set(this.expanded, id, !this.expanded[id]);
        },
        isExpanded(id) {
            return !!this.expanded[id];
        },
        sortedReminders(reminders) {
            if (!reminders || reminders.length === 0) {
                return [];
            }

            return [...reminders].sort((a, b) => {
                const aTime = a.sent_at ? new Date(a.sent_at).getTime() : 0;
                const bTime = b.sent_at ? new Date(b.sent_at).getTime() : 0;

                return bTime - aTime;
            });
        },
        latestReminder(expert) {
            const sorted = this.sortedReminders(expert.reminders);

            return sorted.length ? sorted[0] : null;
        },
        reminderButtonExpert(expert) {
            return {
                id: expert.expert_id,
                terms_signed_at: expert.terms_signed_at,
                terms_reminder_count: expert.reminder_count,
            };
        },
        getExpertUrl(expertId) {
            return `/admin/experts/view/${expertId}`;
        },
        getProjectUrl(projectId) {
            return `/admin/project/details/${projectId}`;
        },
        formatLocalTime(utc) {
            const date = new Date(utc);
            if (Number.isNaN(date.getTime())) {
                return "N/A";
            }

            const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
            const parts = new Intl.DateTimeFormat("en-US", {
                timeZone: tz,
                weekday: "short",
                day: "numeric",
                month: "short",
                year: "numeric",
                hour: "numeric",
                minute: "2-digit",
                hour12: true,
            }).formatToParts(date);

            const map = {};
            parts.forEach((part) => {
                map[part.type] = part.value;
            });

            return `${map.weekday} ${map.day} ${map.month} ${map.year} ${map.hour}:${map.minute} ${map.dayPeriod}`;
        },
    },
};
</script>

<style scoped>
.expert-reminder-card {
    border-radius: 14px;
    overflow: hidden;
}

.expert-toggle {
    background: #fff;
    cursor: pointer;
    padding: 1.15rem 1.25rem;
    width: 100%;
}

.expert-link {
    color: #4c78dd;
    text-decoration: none;
}

.expert-link:hover {
    color: #3a5fc7;
    text-decoration: underline;
}

.project-link {
    color: #4c78dd;
    text-decoration: none;
}

.project-link:hover {
    color: #3a5fc7;
    text-decoration: underline;
}

.expert-icon {
    align-items: center;
    background: #eef2fb;
    border-radius: 11px;
    color: #4c78dd;
    display: inline-flex;
    height: 45px;
    justify-content: center;
    width: 45px;
}

.expert-name {
    font-size: 1rem;
    font-weight: 700;
}

.expert-meta {
    color: #77848b;
    font-size: .83rem;
}

.reminder-count-badge {
    background: #f0f4fd;
    color: #2557c7;
    font-size: .85rem;
    font-weight: 700;
    padding: .38rem .8rem;
}

.chevron {
    color: #7e8c92;
    margin-left: .4rem;
}

.reminder-detail-table thead th {
    background: #f7f9f8;
    border-bottom: 1px solid #e8ecec;
    color: #718088;
    font-size: .73rem;
    font-weight: 700;
    letter-spacing: .07em;
    padding: .8rem 1.25rem;
    text-transform: uppercase;
}

.reminder-detail-table tbody td {
    border-color: #eef1f1;
    padding: .9rem 1.25rem;
}
</style>
